import { type FC } from "react";
import { useCallback } from "react";
import { useRunner } from "@/queries/runner-queries";
import { RunnerPhase } from "gitpod-next-api/gitpod/v1/runner_pb";
import { TrackLocations } from "@/hooks/use-segment";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { useNavigate, useParams } from "react-router-dom";
import { TimelineContentContainer } from "@/components/onboarding/TimelineContentContainer";
import { LinuxRunnerCreate } from "@/components/runners/details/LinuxRunnerSetup";
import { Heading2 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { Banner } from "@/components/flexkit/Banner";
import { RunnerInfoCard } from "@/components/onboarding/RunnerInfoCard";
import { useRunnerConfigurationSchema } from "@/queries/runner-configuration-queries";

export const InstallLinuxRunnerPage: FC = () => {
    const params = useParams();
    const navigate = useNavigate();
    const { data: runner } = useRunner(params.runnerId);
    const { data: schema } = useRunnerConfigurationSchema(params.runnerId);

    const isActive = runner?.status?.phase === RunnerPhase.ACTIVE;
    const hasConfigSchema = !!schema?.scm?.length;

    const onBack = useCallback(() => {
        navigate("/onboarding/set-up-runner/remote/configure/create-linux");
    }, [navigate]);

    const onNext = useCallback(() => {
        navigate(`/onboarding/set-up-runner/remote/configure/${params.runnerId}/source-control-access`);
    }, [navigate, params.runnerId]);

    return (
        <TimelineContentContainer
            onBack={onBack}
            onNextDisabled={!(isActive && hasConfigSchema)}
            onNext={onNext}
            data-track-location={TrackLocations.OnboardingRunnerConfigureLinuxTabStep}
        >
            <SkeletonBlock ready={Boolean(runner)} className="h-[150px]">
                {runner && (
                    <div className="flex w-full flex-col gap-4">
                        <Heading2>Install Linux Runner</Heading2>

                        <div className="flex flex-col gap-8 lg:flex-row">
                            {/* Left side: Setup instructions */}
                            <div className="flex flex-col gap-4 lg:basis-2/3">
                                <Text className="text-content-secondary">
                                    The installation will set up a runner service that manages development environments
                                    on your Linux machine. This includes the runner daemon, cloud-hypervisor for VM
                                    isolation, and a network bridge for environment connectivity.
                                </Text>
                                <LinuxRunnerCreate runner={runner} />
                            </div>

                            {/* Right side: Status */}
                            <div className="flex flex-col gap-4 lg:basis-1/3">
                                {isActive && hasConfigSchema && <Banner variant="success" text="Runner installed" />}
                                {isActive && !hasConfigSchema && (
                                    <Banner variant="progress" text="Waiting for runner to initialize" />
                                )}
                                <RunnerInfoCard runner={runner} showInstallButton={false} />
                            </div>
                        </div>
                    </div>
                )}
            </SkeletonBlock>
        </TimelineContentContainer>
    );
};
